import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Alert, Spinner, Modal, Form, Nav, Tab } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiGift, FiCalendar, FiMapPin, FiCloud, FiAlertTriangle, FiKey, FiEdit, FiCloudRain } from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'bootstrap/dist/css/bootstrap.css';
import { getActivityClaimById, generateClaimTokenInfo, generateActivityConfirmationLink, generateForecastTriggeredUrl, generateActivityConfirmationToken, generatePaymentLink, updateActivityStatus, updateClaimStatus, confirmActivityStatus, checkMeteoFranceObservations, createOrUpdateMeteostatObservations } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminActivityClaimDetail = () => {
    const navigate = useNavigate();
    const { claimId } = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [claim, setClaim] = useState(location.state?.claim || null);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [tokenError, setTokenError] = useState(null);
    const [tokenData, setTokenData] = useState(null);
    const [showConfirmationLinkModal, setShowConfirmationLinkModal] = useState(false);
    const [confirmationLinkLoading, setConfirmationLinkLoading] = useState(false);
    const [confirmationLinkError, setConfirmationLinkError] = useState(null);
    const [confirmationLinkData, setConfirmationLinkData] = useState(null);
    const [showForecastTriggeredModal, setShowForecastTriggeredModal] = useState(false);
    const [forecastTriggeredLoading, setForecastTriggeredLoading] = useState(false);
    const [forecastTriggeredError, setForecastTriggeredError] = useState(null);
    const [forecastTriggeredData, setForecastTriggeredData] = useState(null);
    const [showActivityConfirmationModal, setShowActivityConfirmationModal] = useState(false);
    const [activityConfirmationLoading, setActivityConfirmationLoading] = useState(false);
    const [activityConfirmationError, setActivityConfirmationError] = useState(null);
    const [activityConfirmationData, setActivityConfirmationData] = useState(null);
    const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
    const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
    const [paymentLinkError, setPaymentLinkError] = useState(null);
    const [paymentLinkData, setPaymentLinkData] = useState(null);

    const [showActivityStatusModal, setShowActivityStatusModal] = useState(false);
    const [activityStatusLoading, setActivityStatusLoading] = useState(false);
    const [activityStatusError, setActivityStatusError] = useState(null);
    const [activityStatusData, setActivityStatusData] = useState(null);
    const [selectedActivityStatus, setSelectedActivityStatus] = useState('');

    const [showClaimStatusModal, setShowClaimStatusModal] = useState(false);
    const [claimStatusLoading, setClaimStatusLoading] = useState(false);
    const [claimStatusError, setClaimStatusError] = useState(null);
    const [claimStatusData, setClaimStatusData] = useState(null);
    const [selectedClaimStatus, setSelectedClaimStatus] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentDatePicker, setPaymentDatePicker] = useState(null);
    const [paymentMode, setPaymentMode] = useState('');

    const [showConfirmActivityModal, setShowConfirmActivityModal] = useState(false);
    const [confirmActivityLoading, setConfirmActivityLoading] = useState(false);
    const [confirmActivityError, setConfirmActivityError] = useState(null);
    const [confirmActivityData, setConfirmActivityData] = useState(null);

    const [activeTab, setActiveTab] = useState('main-info');

    // States for MeteoFrance Observations Check
    const [meteoFranceLoading, setMeteoFranceLoading] = useState(false);
    const [meteoFranceError, setMeteoFranceError] = useState(null);
    const [meteoFranceData, setMeteoFranceData] = useState(null);
    const [showMeteoFranceDetails, setShowMeteoFranceDetails] = useState(false);

    // States for Meteostat Observations Check
    const [meteostatLoading, setMeteostatLoading] = useState(false);
    const [meteostatError, setMeteostatError] = useState(null);
    const [meteostatData, setMeteostatData] = useState(null);
    const [showMeteostatDetails, setShowMeteostatDetails] = useState(false);

    useEffect(() => {
        const fetchClaim = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getActivityClaimById(claimId);
                // API returns { claim: { ... } }
                setClaim(data?.claim || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement du sinistre'));
            } finally {
                setLoading(false);
            }
        };
        // Always fetch by id to ensure up-to-date data
        if (claimId) fetchClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimId]);

    const handleGenerateToken = async () => {
        if (!claimId) {
            setTokenError('ID du sinistre non trouvé');
            return;
        }

        try {
            setTokenLoading(true);
            setTokenError(null);
            setTokenData(null);
            const data = await generateClaimTokenInfo(claimId);
            setTokenData(data);
            setShowTokenModal(true);
        } catch (e) {
            setTokenError(handleApiError(e, 'Erreur lors de la génération du token'));
            setShowTokenModal(true);
        } finally {
            setTokenLoading(false);
        }
    };

    const handleCloseTokenModal = () => {
        setShowTokenModal(false);
        setTokenError(null);
        setTokenData(null);
    };

    const handleGenerateConfirmationLink = async () => {
        const policyCode = claim?.policy_info?.policy_code;
        if (!policyCode) {
            setConfirmationLinkError('Code police non trouvé');
            return;
        }

        try {
            setConfirmationLinkLoading(true);
            setConfirmationLinkError(null);
            setConfirmationLinkData(null);
            const data = await generateActivityConfirmationLink(policyCode);
            setConfirmationLinkData(data);
            setShowConfirmationLinkModal(true);
        } catch (e) {
            setConfirmationLinkError(handleApiError(e, 'Erreur lors de la génération du lien de confirmation'));
            setShowConfirmationLinkModal(true);
        } finally {
            setConfirmationLinkLoading(false);
        }
    };

    const handleGenerateForecastTriggeredUrl = async () => {
        const claimIdFromClaim = claim?.claim_id || claimId;
        if (!claimIdFromClaim) {
            setForecastTriggeredError('ID du sinistre non trouvé');
            return;
        }

        try {
            setForecastTriggeredLoading(true);
            setForecastTriggeredError(null);
            setForecastTriggeredData(null);
            const data = await generateForecastTriggeredUrl(claimIdFromClaim);
            setForecastTriggeredData(data);
            setShowForecastTriggeredModal(true);
        } catch (e) {
            setForecastTriggeredError(handleApiError(e, 'Erreur lors de la génération de l\'URL de prévision déclenchée'));
            setShowForecastTriggeredModal(true);
        } finally {
            setForecastTriggeredLoading(false);
        }
    };

    const handleCloseForecastTriggeredModal = () => {
        setShowForecastTriggeredModal(false);
        setForecastTriggeredError(null);
        setForecastTriggeredData(null);
    };

    const handleGenerateActivityConfirmationToken = async () => {
        const claimIdFromClaim = claim?.claim_id || claimId;
        if (!claimIdFromClaim) {
            setActivityConfirmationError('ID du sinistre non trouvé');
            return;
        }

        try {
            setActivityConfirmationLoading(true);
            setActivityConfirmationError(null);
            setActivityConfirmationData(null);
            const data = await generateActivityConfirmationToken(claimIdFromClaim);
            setActivityConfirmationData(data);
            setShowActivityConfirmationModal(true);
        } catch (e) {
            setActivityConfirmationError(handleApiError(e, 'Erreur lors de la génération du token de confirmation d\'activité'));
            setShowActivityConfirmationModal(true);
        } finally {
            setActivityConfirmationLoading(false);
        }
    };

    const handleCloseActivityConfirmationModal = () => {
        setShowActivityConfirmationModal(false);
        setActivityConfirmationError(null);
        setActivityConfirmationData(null);
    };

    const handleGeneratePaymentLink = async () => {
        const policyCode = claim?.policy_info?.policy_code;
        if (!policyCode) {
            setPaymentLinkError('Code police non trouvé');
            return;
        }

        try {
            setPaymentLinkLoading(true);
            setPaymentLinkError(null);
            setPaymentLinkData(null);
            const data = await generatePaymentLink(policyCode);
            setPaymentLinkData(data);
            setShowPaymentLinkModal(true);
        } catch (e) {
            setPaymentLinkError(handleApiError(e, 'Erreur lors de la génération du lien de paiement'));
            setShowPaymentLinkModal(true);
        } finally {
            setPaymentLinkLoading(false);
        }
    };

    const handleClosePaymentLinkModal = () => {
        setShowPaymentLinkModal(false);
        setPaymentLinkError(null);
        setPaymentLinkData(null);
    };

    const handleOpenActivityStatusModal = () => {
        setShowActivityStatusModal(true);
        setSelectedActivityStatus(claim?.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité');
        setActivityStatusError(null);
        setActivityStatusData(null);
    };

    const handleCloseActivityStatusModal = () => {
        setShowActivityStatusModal(false);
        setActivityStatusError(null);
        setActivityStatusData(null);
        setSelectedActivityStatus('');
    };

    const handleUpdateActivityStatus = async () => {
        if (!claimId) {
            setActivityStatusError('ID du sinistre non trouvé');
            return;
        }

        if (!selectedActivityStatus) {
            setActivityStatusError('Veuillez sélectionner un statut d\'activité');
            return;
        }

        try {
            setActivityStatusLoading(true);
            setActivityStatusError(null);
            setActivityStatusData(null);
            const data = await updateActivityStatus(claimId, selectedActivityStatus);
            setActivityStatusData(data);

            // Rafraîchir les données du sinistre après la mise à jour
            const updatedClaim = await getActivityClaimById(claimId);
            setClaim(updatedClaim?.claim || null);
        } catch (e) {
            setActivityStatusError(handleApiError(e, 'Erreur lors de la mise à jour du statut d\'activité'));
        } finally {
            setActivityStatusLoading(false);
        }
    };

    const handleOpenClaimStatusModal = () => {
        setShowClaimStatusModal(true);
        setSelectedClaimStatus(claim?.claim_status || '');
        setPaymentDate('');
        setPaymentDatePicker(null);
        setPaymentMode('');
        setClaimStatusError(null);
        setClaimStatusData(null);
    };

    const handleCloseClaimStatusModal = () => {
        setShowClaimStatusModal(false);
        setClaimStatusError(null);
        setClaimStatusData(null);
        setSelectedClaimStatus('');
        setPaymentDate('');
        setPaymentDatePicker(null);
        setPaymentMode('');
    };

    // Fonction pour convertir Date en format dd/mm/yyyy
    const formatDateToString = (date) => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Fonction pour convertir dd/mm/yyyy en Date
    const parseStringToDate = (dateString) => {
        if (!dateString) return null;
        const parts = dateString.split('/');
        if (parts.length !== 3) return null;
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
    };

    // Gérer le changement de date dans le datepicker
    const handlePaymentDateChange = (date) => {
        setPaymentDatePicker(date);
        if (date) {
            setPaymentDate(formatDateToString(date));
        } else {
            setPaymentDate('');
        }
    };

    const handleOpenConfirmActivityModal = () => {
        setShowConfirmActivityModal(true);
        setConfirmActivityError(null);
        setConfirmActivityData(null);
    };

    const handleCloseConfirmActivityModal = () => {
        setShowConfirmActivityModal(false);
        setConfirmActivityError(null);
        setConfirmActivityData(null);
    };

    const handleConfirmActivityStatus = async () => {
        if (!claimId) {
            setConfirmActivityError('ID du sinistre non trouvé');
            return;
        }

        try {
            setConfirmActivityLoading(true);
            setConfirmActivityError(null);
            setConfirmActivityData(null);
            const data = await confirmActivityStatus(claimId);
            setConfirmActivityData(data);

            // Rafraîchir les données du sinistre après la confirmation
            const updatedClaim = await getActivityClaimById(claimId);
            setClaim(updatedClaim?.claim || null);
        } catch (e) {
            setConfirmActivityError(handleApiError(e, 'Erreur lors de la confirmation du statut d\'activité'));
        } finally {
            setConfirmActivityLoading(false);
        }
    };

    const handleCheckMeteoFranceObservations = async () => {
        if (!claimId) {
            setMeteoFranceError('ID du sinistre non trouvé');
            return;
        }

        try {
            setMeteoFranceLoading(true);
            setMeteoFranceError(null);
            setMeteoFranceData(null);
            const data = await checkMeteoFranceObservations(claimId);
            setMeteoFranceData(data);

            // Rafraîchir les données du sinistre après la vérification
            const updatedClaim = await getActivityClaimById(claimId);
            setClaim(updatedClaim?.claim || null);
        } catch (e) {
            setMeteoFranceError(handleApiError(e, 'Erreur lors de la vérification des observations MétéoFrance'));
        } finally {
            setMeteoFranceLoading(false);
        }
    };

    const handleCheckMeteostatObservations = async () => {
        if (!claimId) {
            setMeteostatError('ID du sinistre non trouvé');
            return;
        }

        try {
            setMeteostatLoading(true);
            setMeteostatError(null);
            setMeteostatData(null);
            const data = await createOrUpdateMeteostatObservations(claimId);
            setMeteostatData(data);

            // Rafraîchir les données du sinistre après la vérification
            const updatedClaim = await getActivityClaimById(claimId);
            setClaim(updatedClaim?.claim || null);
        } catch (e) {
            setMeteostatError(handleApiError(e, 'Erreur lors de la création/mise à jour des observations Meteostat'));
        } finally {
            setMeteostatLoading(false);
        }
    };

    const handleUpdateClaimStatus = async () => {
        if (!claimId) {
            setClaimStatusError('ID du sinistre non trouvé');
            return;
        }

        if (!selectedClaimStatus) {
            setClaimStatusError('Veuillez sélectionner un statut de sinistre');
            return;
        }

        // Vérifier si payment_date et payment_mode sont requis pour le statut "Paid"
        if (selectedClaimStatus === 'Paid') {
            if (!paymentDate || !paymentMode) {
                setClaimStatusError('La date de paiement et le mode de paiement sont requis lorsque le statut est "Paid"');
                return;
            }
        }

        try {
            setClaimStatusLoading(true);
            setClaimStatusError(null);
            setClaimStatusData(null);

            const updateData = {
                claim_status: selectedClaimStatus
            };

            if (selectedClaimStatus === 'Paid') {
                updateData.payment_date = paymentDate;
                updateData.payment_mode = paymentMode;
            }

            const data = await updateClaimStatus(claimId, updateData);
            setClaimStatusData(data);

            // Rafraîchir les données du sinistre après la mise à jour
            const updatedClaim = await getActivityClaimById(claimId);
            setClaim(updatedClaim?.claim || null);
        } catch (e) {
            setClaimStatusError(handleApiError(e, 'Erreur lors de la mise à jour du statut du sinistre'));
        } finally {
            setClaimStatusLoading(false);
        }
    };

    const handleCloseConfirmationLinkModal = () => {
        setShowConfirmationLinkModal(false);
        setConfirmationLinkError(null);
        setConfirmationLinkData(null);
    };

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du sinistre...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!claim) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du sinistre</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de sinistre disponible pour l'identifiant: {claimId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiAlertTriangle className="me-2" />
                    {t('pages.activityClaimDetail.title')}
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> {t('common.back')}
                </Button>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={(k) => {
                setActiveTab(k || 'main-info');
                // Initialiser les états quand on change d'onglet
                if (k === 'update-activity-status' && !selectedActivityStatus) {
                    setSelectedActivityStatus(claim?.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité');
                }
                if (k === 'update-claim-status' && !selectedClaimStatus) {
                    setSelectedClaimStatus(claim?.claim_status || '');
                }
            }}>
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="main-info">
                            {t('pages.activityClaimDetail.tabs.mainInfo')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="token-info">
                            <FiKey className="me-2" />
                            {t('pages.activityClaimDetail.tabs.tokenInfo')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="confirmation-link">
                            <FiCheckCircle className="me-2" />
                            {t('pages.activityClaimDetail.tabs.confirmationLink')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="forecast-triggered">
                            <FiCloud className="me-2" />
                            {t('pages.activityClaimDetail.tabs.forecastTriggered')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="activity-confirmation">
                            <FiCheckCircle className="me-2" />
                            {t('pages.activityClaimDetail.tabs.activityConfirmation')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="payment-link">
                            <FiGift className="me-2" />
                            {t('pages.activityClaimDetail.tabs.paymentLink')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="update-activity-status">
                            <FiEdit className="me-2" />
                            {t('pages.activityClaimDetail.tabs.updateActivityStatus')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="update-claim-status">
                            <FiEdit className="me-2" />
                            {t('pages.activityClaimDetail.tabs.updateClaimStatus')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="confirm-activity">
                            <FiCheckCircle className="me-2" />
                            {t('pages.activityClaimDetail.tabs.confirmActivityStatus')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="meteofrance-check">
                            <FiCloudRain className="me-2" />
                            {t('pages.activityClaimDetail.tabs.meteofranceCheck')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="meteostat-check">
                            <FiCloudRain className="me-2" />
                            {t('pages.activityClaimDetail.tabs.meteostatCheck')}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="token-info">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Token info sinistre</h5>
                                <Button
                                    variant="primary"
                                    onClick={handleGenerateToken}
                                    disabled={tokenLoading || !claimId}
                                >
                                    {tokenLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <FiKey className="me-2" />
                                            Générer le token
                                        </>
                                    )}
                                </Button>
                                {tokenError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {tokenError}
                                    </Alert>
                                )}
                                {tokenData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>Token généré avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations du token</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {tokenData.token && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>Token :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={tokenData.token}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(tokenData.token);
                                                            }}
                                                        >
                                                            Copier le token
                                                        </Button>
                                                    </div>
                                                )}
                                                {tokenData.url && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>URL d'accès :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={tokenData.url}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(tokenData.url);
                                                            }}
                                                        >
                                                            Copier l'URL
                                                        </Button>
                                                    </div>
                                                )}
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    fontSize: '0.85rem',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '1rem',
                                                    borderRadius: '0.25rem'
                                                }}>
                                                    {JSON.stringify(tokenData, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="confirmation-link">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Lien confirmation activité</h5>
                                <Button
                                    variant="success"
                                    onClick={handleGenerateConfirmationLink}
                                    disabled={confirmationLinkLoading || !claim?.policy_info?.policy_code}
                                >
                                    {confirmationLinkLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <FiCheckCircle className="me-2" />
                                            Générer le lien
                                        </>
                                    )}
                                </Button>
                                {confirmationLinkError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {confirmationLinkError}
                                    </Alert>
                                )}
                                {confirmationLinkData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>Lien généré avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations du lien</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {confirmationLinkData.url && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>URL de confirmation :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={confirmationLinkData.url}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2 me-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(confirmationLinkData.url);
                                                            }}
                                                        >
                                                            Copier le lien
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                window.open(confirmationLinkData.url, '_blank');
                                                            }}
                                                        >
                                                            Ouvrir dans un nouvel onglet
                                                        </Button>
                                                    </div>
                                                )}
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    fontSize: '0.85rem',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '1rem',
                                                    borderRadius: '0.25rem'
                                                }}>
                                                    {JSON.stringify(confirmationLinkData, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="forecast-triggered">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">URL prévision déclenchée</h5>
                                <Button
                                    variant="info"
                                    onClick={handleGenerateForecastTriggeredUrl}
                                    disabled={forecastTriggeredLoading || !claimId}
                                >
                                    {forecastTriggeredLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <FiCloud className="me-2" />
                                            Générer l'URL
                                        </>
                                    )}
                                </Button>
                                {forecastTriggeredError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {forecastTriggeredError}
                                    </Alert>
                                )}
                                {forecastTriggeredData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>URL générée avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations de l'URL</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {forecastTriggeredData.forecast_triggered_url && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>URL de prévision déclenchée :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={forecastTriggeredData.forecast_triggered_url}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2 me-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(forecastTriggeredData.forecast_triggered_url);
                                                            }}
                                                        >
                                                            Copier l'URL
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                window.open(forecastTriggeredData.forecast_triggered_url, '_blank');
                                                            }}
                                                        >
                                                            Ouvrir dans un nouvel onglet
                                                        </Button>
                                                    </div>
                                                )}
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    fontSize: '0.85rem',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '1rem',
                                                    borderRadius: '0.25rem'
                                                }}>
                                                    {JSON.stringify(forecastTriggeredData, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="activity-confirmation">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Token confirmation activité</h5>
                                <Button
                                    variant="warning"
                                    onClick={handleGenerateActivityConfirmationToken}
                                    disabled={activityConfirmationLoading || !claimId}
                                >
                                    {activityConfirmationLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <FiCheckCircle className="me-2" />
                                            Générer le token
                                        </>
                                    )}
                                </Button>
                                {activityConfirmationError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {activityConfirmationError}
                                    </Alert>
                                )}
                                {activityConfirmationData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>Token généré avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations du token</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {activityConfirmationData.validation_token && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>Token de validation :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={activityConfirmationData.validation_token}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(activityConfirmationData.validation_token);
                                                            }}
                                                        >
                                                            Copier le token
                                                        </Button>
                                                    </div>
                                                )}
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    fontSize: '0.85rem',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '1rem',
                                                    borderRadius: '0.25rem'
                                                }}>
                                                    {JSON.stringify(activityConfirmationData, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="payment-link">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Lien paiement</h5>
                                <Button
                                    variant="secondary"
                                    onClick={handleGeneratePaymentLink}
                                    disabled={paymentLinkLoading || !claim?.policy_info?.policy_code}
                                >
                                    {paymentLinkLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Génération...
                                        </>
                                    ) : (
                                        <>
                                            <FiGift className="me-2" />
                                            Générer le lien
                                        </>
                                    )}
                                </Button>
                                {paymentLinkError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {paymentLinkError}
                                    </Alert>
                                )}
                                {paymentLinkData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>Lien de paiement généré avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations du paiement</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {paymentLinkData.payment && paymentLinkData.payment.payment_link && (
                                                    <div className="mb-3">
                                                        <Form.Label><strong>Lien de paiement Stripe :</strong></Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            value={paymentLinkData.payment.payment_link}
                                                            readOnly
                                                            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                        />
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="mt-2 me-2"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(paymentLinkData.payment.payment_link);
                                                            }}
                                                        >
                                                            Copier le lien
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="mt-2"
                                                            onClick={() => {
                                                                window.open(paymentLinkData.payment.payment_link, '_blank');
                                                            }}
                                                        >
                                                            Ouvrir dans un nouvel onglet
                                                        </Button>
                                                    </div>
                                                )}
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    fontSize: '0.85rem',
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '1rem',
                                                    borderRadius: '0.25rem'
                                                }}>
                                                    {JSON.stringify(paymentLinkData, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="update-activity-status">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Choix du client annuler / maintenir</h5>

                                {activityStatusLoading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Mise à jour du statut en cours...</p>
                                    </div>
                                ) : activityStatusError ? (
                                    <Alert variant="danger">
                                        <strong>Erreur :</strong> {activityStatusError}
                                    </Alert>
                                ) : activityStatusData ? (
                                    <div>
                                        <Alert variant="success" className="mb-3">
                                            <strong>Statut d'activité mis à jour avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations mises à jour</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {activityStatusData.claim_id && (
                                                    <p className="mb-2">
                                                        <strong>ID du sinistre :</strong> {activityStatusData.claim_id}
                                                    </p>
                                                )}
                                                {activityStatusData.activity_status && (
                                                    <p className="mb-2">
                                                        <strong>Statut d'activité :</strong> {activityStatusData.activity_status}
                                                    </p>
                                                )}
                                                {activityStatusData.amount_of_claim !== undefined && (
                                                    <p className="mb-2">
                                                        <strong>Montant du sinistre :</strong> {activityStatusData.amount_of_claim} €
                                                    </p>
                                                )}
                                                {activityStatusData.updated_at && (
                                                    <p className="mb-2">
                                                        <strong>Date de mise à jour :</strong> {activityStatusData.updated_at}
                                                    </p>
                                                )}
                                                {activityStatusData.message && (
                                                    <Alert variant="info" className="mt-3">
                                                        {activityStatusData.message}
                                                    </Alert>
                                                )}
                                                <div className="mt-3">
                                                    <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                    <pre style={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.85rem',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '1rem',
                                                        borderRadius: '0.25rem'
                                                    }}>
                                                        {JSON.stringify(activityStatusData, null, 2)}
                                                    </pre>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                        <Button
                                            variant="primary"
                                            className="mt-3"
                                            onClick={() => {
                                                setActivityStatusData(null);
                                                setSelectedActivityStatus(claim?.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité');
                                            }}
                                        >
                                            Modifier à nouveau
                                        </Button>
                                    </div>
                                ) : (
                                    <Form>
                                        <Form.Group className="mb-3">
                                            <Form.Label><strong>Sélectionner le statut d'activité :</strong></Form.Label>
                                            <Form.Select
                                                value={selectedActivityStatus}
                                                onChange={(e) => setSelectedActivityStatus(e.target.value)}
                                                required
                                            >
                                                <option value="">Sélectionner un statut...</option>
                                                <option value="Maintenir mon activité">Maintenir mon activité</option>
                                                <option value="Annuler mon activité">Annuler mon activité</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Sélectionnez si l'activité doit être maintenue ou annulée pour ce sinistre.
                                            </Form.Text>
                                        </Form.Group>
                                        {claim && (
                                            <Alert variant="info" className="mt-3">
                                                <strong>Statut actuel :</strong> {claim.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité'}
                                            </Alert>
                                        )}
                                        <div className="mt-3">
                                            <Button
                                                variant="primary"
                                                onClick={handleUpdateActivityStatus}
                                                disabled={!selectedActivityStatus || activityStatusLoading}
                                            >
                                                {activityStatusLoading ? (
                                                    <>
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Mise à jour...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiEdit className="me-2" />
                                                        Mettre à jour
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="update-claim-status">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Mettre à jour le statut du sinistre</h5>

                                {claimStatusLoading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Mise à jour du statut en cours...</p>
                                    </div>
                                ) : claimStatusError ? (
                                    <Alert variant="danger">
                                        <strong>Erreur :</strong> {claimStatusError}
                                    </Alert>
                                ) : claimStatusData ? (
                                    <div>
                                        <Alert variant="success" className="mb-3">
                                            <strong>Statut du sinistre mis à jour avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations mises à jour</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {claimStatusData.claim_id && (
                                                    <p className="mb-2">
                                                        <strong>ID du sinistre :</strong> {claimStatusData.claim_id}
                                                    </p>
                                                )}
                                                {claimStatusData.claim_status && (
                                                    <p className="mb-2">
                                                        <strong>Statut du sinistre :</strong> {claimStatusData.claim_status}
                                                    </p>
                                                )}
                                                {claimStatusData.claim_stage && (
                                                    <p className="mb-2">
                                                        <strong>Étape du sinistre :</strong> {claimStatusData.claim_stage}
                                                    </p>
                                                )}
                                                {claimStatusData.payment_date && (
                                                    <p className="mb-2">
                                                        <strong>Date de paiement :</strong> {claimStatusData.payment_date}
                                                    </p>
                                                )}
                                                {claimStatusData.payment_mode && (
                                                    <p className="mb-2">
                                                        <strong>Mode de paiement :</strong> {claimStatusData.payment_mode}
                                                    </p>
                                                )}
                                                {claimStatusData.updated_at && (
                                                    <p className="mb-2">
                                                        <strong>Date de mise à jour :</strong> {claimStatusData.updated_at}
                                                    </p>
                                                )}
                                                {claimStatusData.updated_by && (
                                                    <p className="mb-2">
                                                        <strong>Mis à jour par :</strong> {claimStatusData.updated_by}
                                                    </p>
                                                )}
                                                {claimStatusData.message && (
                                                    <Alert variant="info" className="mt-3">
                                                        {claimStatusData.message}
                                                    </Alert>
                                                )}
                                                <div className="mt-3">
                                                    <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                    <pre style={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.85rem',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '1rem',
                                                        borderRadius: '0.25rem'
                                                    }}>
                                                        {JSON.stringify(claimStatusData, null, 2)}
                                                    </pre>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                        <Button
                                            variant="warning"
                                            className="mt-3"
                                            onClick={() => {
                                                setClaimStatusData(null);
                                                setSelectedClaimStatus(claim?.claim_status || '');
                                                setPaymentDate('');
                                                setPaymentDatePicker(null);
                                                setPaymentMode('');
                                            }}
                                        >
                                            Modifier à nouveau
                                        </Button>
                                    </div>
                                ) : (
                                    <Form>
                                        <Form.Group className="mb-3">
                                            <Form.Label><strong>Sélectionner le statut du sinistre :</strong></Form.Label>
                                            <Form.Select
                                                value={selectedClaimStatus}
                                                onChange={(e) => {
                                                    setSelectedClaimStatus(e.target.value);
                                                    if (e.target.value !== 'Paid') {
                                                        setPaymentDate('');
                                                        setPaymentDatePicker(null);
                                                        setPaymentMode('');
                                                    }
                                                }}
                                                required
                                            >
                                                <option value="">Sélectionner un statut...</option>
                                                <option value="Pre-validated">Pre-validated</option>
                                                <option value="Not pre-validated">Not pre-validated</option>
                                                <option value="Validated">Validated</option>
                                                <option value="Not validated">Not validated</option>
                                                <option value="Documents received">Documents received</option>
                                                <option value="Paid">Paid</option>
                                                <option value="Expired">Expired</option>
                                                <option value="No answer yet">No answer yet</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Sélectionnez le nouveau statut du sinistre. Si vous choisissez "Paid", vous devrez fournir la date et le mode de paiement.
                                            </Form.Text>
                                        </Form.Group>

                                        {selectedClaimStatus === 'Paid' && (
                                            <>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><strong>Date de paiement (dd/mm/yyyy) :</strong></Form.Label>
                                                    <DatePicker
                                                        selected={paymentDatePicker}
                                                        onChange={handlePaymentDateChange}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="Sélectionner une date"
                                                        className="form-control"
                                                        required
                                                        isClearable
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        maxDate={new Date()}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Sélectionnez la date de paiement (format : jj/mm/aaaa)
                                                    </Form.Text>
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label><strong>Mode de paiement :</strong></Form.Label>
                                                    <Form.Select
                                                        value={paymentMode}
                                                        onChange={(e) => setPaymentMode(e.target.value)}
                                                        required
                                                    >
                                                        <option value="">Sélectionner un mode de paiement...</option>
                                                        <option value="strip">Stripe</option>
                                                        <option value="voucher">Voucher</option>
                                                        <option value="cash">Cash</option>
                                                        <option value="transfer">Transfer</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </>
                                        )}

                                        {claim && (
                                            <Alert variant="info" className="mt-3">
                                                <strong>Statut actuel :</strong> {claim.claim_status || 'N/A'}<br />
                                                <strong>Étape actuelle :</strong> {claim.claim_stage || 'N/A'}
                                            </Alert>
                                        )}
                                        <div className="mt-3">
                                            <Button
                                                variant="primary"
                                                onClick={handleUpdateClaimStatus}
                                                disabled={!selectedClaimStatus || claimStatusLoading || (selectedClaimStatus === 'Paid' && (!paymentDate || !paymentMode))}
                                            >
                                                {claimStatusLoading ? (
                                                    <>
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        Mise à jour...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiEdit className="me-2" />
                                                        Mettre à jour
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="confirm-activity">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">Confirmer le statut d'activité</h5>

                                {confirmActivityLoading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Confirmation du statut d'activité en cours...</p>
                                    </div>
                                ) : confirmActivityError ? (
                                    <Alert variant="danger">
                                        <strong>Erreur :</strong> {confirmActivityError}
                                    </Alert>
                                ) : confirmActivityData ? (
                                    <div>
                                        <Alert variant="success" className="mb-3">
                                            <strong>Statut d'activité confirmé avec succès !</strong>
                                        </Alert>
                                        <Card>
                                            <Card.Header>
                                                <strong>Informations de confirmation</strong>
                                            </Card.Header>
                                            <Card.Body>
                                                {confirmActivityData.claim_id && (
                                                    <p className="mb-2">
                                                        <strong>ID du sinistre :</strong> {confirmActivityData.claim_id}
                                                    </p>
                                                )}
                                                {confirmActivityData.is_confirmed !== undefined && (
                                                    <p className="mb-2">
                                                        <strong>Statut de confirmation :</strong> {confirmActivityData.is_confirmed ? 'Confirmé' : 'Non confirmé'}
                                                    </p>
                                                )}
                                                {confirmActivityData.choice_confirmation_date && (
                                                    <p className="mb-2">
                                                        <strong>Date de confirmation du choix :</strong> {confirmActivityData.choice_confirmation_date}
                                                    </p>
                                                )}
                                                {confirmActivityData.updated_at && (
                                                    <p className="mb-2">
                                                        <strong>Date de mise à jour :</strong> {confirmActivityData.updated_at}
                                                    </p>
                                                )}
                                                {confirmActivityData.updated_by && (
                                                    <p className="mb-2">
                                                        <strong>Confirmé par :</strong> {confirmActivityData.updated_by}
                                                    </p>
                                                )}
                                                {confirmActivityData.message && (
                                                    <Alert variant="info" className="mt-3">
                                                        {confirmActivityData.message}
                                                    </Alert>
                                                )}
                                                <div className="mt-3">
                                                    <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                    <pre style={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.85rem',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '1rem',
                                                        borderRadius: '0.25rem'
                                                    }}>
                                                        {JSON.stringify(confirmActivityData, null, 2)}
                                                    </pre>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                ) : (
                                    <div>
                                        <Alert variant="warning" className="mb-3">
                                            <strong>Attention :</strong> Cette action va confirmer le statut d'activité du sinistre.
                                        </Alert>
                                        {claim && (
                                            <Card className="mb-3">
                                                <Card.Header>
                                                    <strong>Informations du sinistre</strong>
                                                </Card.Header>
                                                <Card.Body>
                                                    <p className="mb-2">
                                                        <strong>ID du sinistre :</strong> {claim.claim_id || claimId}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Statut actuel de confirmation :</strong> {claim.is_confirmed ? 'Confirmé' : 'Non confirmé'}
                                                    </p>
                                                    {claim.activity_maintained && (
                                                        <p className="mb-2">
                                                            <strong>Choix du client :</strong> {claim.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité'}
                                                        </p>
                                                    )}
                                                    {claim.choice_date && (
                                                        <p className="mb-2">
                                                            <strong>Date du choix :</strong> {claim.choice_date}
                                                        </p>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        )}
                                        <Button
                                            variant="success"
                                            onClick={handleConfirmActivityStatus}
                                            disabled={confirmActivityLoading || !claimId}
                                        >
                                            {confirmActivityLoading ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" />
                                                    Confirmation...
                                                </>
                                            ) : (
                                                <>
                                                    <FiCheckCircle className="me-2" />
                                                    Confirmer le statut d'activité
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="meteofrance-check">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">
                                    <FiCloudRain className="me-2" />
                                    {t('pages.activityClaimDetail.meteofrance.title')}
                                </h5>
                                <p className="text-muted mb-3">
                                    {t('pages.activityClaimDetail.meteofrance.description')}
                                </p>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Button
                                        variant="primary"
                                        onClick={handleCheckMeteoFranceObservations}
                                        disabled={meteoFranceLoading || !claimId}
                                    >
                                        {meteoFranceLoading ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                {t('pages.activityClaimDetail.meteofrance.checking')}
                                            </>
                                        ) : (
                                            <>
                                                <FiCloudRain className="me-2" />
                                                {t('pages.activityClaimDetail.buttons.checkObservations')}
                                            </>
                                        )}
                                    </Button>
                                    {meteoFranceData && (
                                        <Form.Check
                                            type="switch"
                                            id="toggle-meteofrance-details"
                                            label={t('pages.activityClaimDetail.messages.showDetails')}
                                            checked={showMeteoFranceDetails}
                                            onChange={(e) => setShowMeteoFranceDetails(e.target.checked)}
                                        />
                                    )}
                                </div>
                                {meteoFranceError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {meteoFranceError}
                                    </Alert>
                                )}
                                {meteoFranceData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>{t('pages.activityClaimDetail.messages.verificationComplete')}</strong>
                                        </Alert>
                                        {showMeteoFranceDetails ? (
                                            <Card>
                                                <Card.Header>
                                                    <strong>Résultats de la vérification</strong>
                                                </Card.Header>
                                                <Card.Body>
                                                    {meteoFranceData.claim_id && (
                                                        <p className="mb-2">
                                                            <strong>ID du sinistre :</strong> {meteoFranceData.claim_id}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.observation_id && (
                                                        <p className="mb-2">
                                                            <strong>ID de l'observation :</strong> {meteoFranceData.observation_id}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.is_triggered_observation !== undefined && (
                                                        <p className="mb-2">
                                                            <strong>Observation déclenchée :</strong>
                                                            <Badge bg={meteoFranceData.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                {meteoFranceData.is_triggered_observation ? 'Oui' : 'Non'}
                                                            </Badge>
                                                        </p>
                                                    )}
                                                    {meteoFranceData.data_source && (
                                                        <p className="mb-2">
                                                            <strong>Source des données :</strong> {meteoFranceData.data_source}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.total_mm_prc !== undefined && (
                                                        <p className="mb-2">
                                                            <strong>Total précipitations :</strong> {meteoFranceData.total_mm_prc} mm
                                                        </p>
                                                    )}
                                                    {meteoFranceData.station_name && (
                                                        <p className="mb-2">
                                                            <strong>Station météorologique :</strong> {meteoFranceData.station_name}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.claim_status && (
                                                        <p className="mb-2">
                                                            <strong>Statut du sinistre :</strong> {meteoFranceData.claim_status}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.claim_stage && (
                                                        <p className="mb-2">
                                                            <strong>Étape du sinistre :</strong> {meteoFranceData.claim_stage}
                                                        </p>
                                                    )}
                                                    {meteoFranceData.message && (
                                                        <Alert variant="info" className="mt-3">
                                                            {meteoFranceData.message}
                                                        </Alert>
                                                    )}
                                                    <div className="mt-3">
                                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                        <pre style={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            fontSize: '0.85rem',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto',
                                                            backgroundColor: '#f8f9fa',
                                                            padding: '1rem',
                                                            borderRadius: '0.25rem'
                                                        }}>
                                                            {JSON.stringify(meteoFranceData, null, 2)}
                                                        </pre>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        ) : (
                                            <Card>
                                                <Card.Body>
                                                    <Row>
                                                        <Col md={6}>
                                                            <p className="mb-2">
                                                                <strong>Observation déclenchée :</strong>
                                                                <Badge bg={meteoFranceData.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                    {meteoFranceData.is_triggered_observation ? 'Oui' : 'Non'}
                                                                </Badge>
                                                            </p>
                                                        </Col>
                                                        {meteoFranceData.data_source && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Source :</strong> {meteoFranceData.data_source}
                                                                </p>
                                                            </Col>
                                                        )}
                                                        {meteoFranceData.total_mm_prc !== undefined && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Précipitations :</strong> {meteoFranceData.total_mm_prc} mm
                                                                </p>
                                                            </Col>
                                                        )}
                                                        {meteoFranceData.claim_status && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Statut :</strong> {meteoFranceData.claim_status}
                                                                </p>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                    {meteoFranceData.message && (
                                                        <Alert variant="info" className="mt-3">
                                                            {meteoFranceData.message}
                                                        </Alert>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="meteostat-check">
                        <Card>
                            <Card.Body>
                                <h5 className="mb-3">
                                    <FiCloudRain className="me-2" />
                                    Créer ou mettre à jour les observations Meteostat
                                </h5>
                                <p className="text-muted mb-3">
                                    Cette fonction crée ou met à jour les observations météorologiques Meteostat pour ce sinistre.
                                    Elle récupère les données depuis l'API Meteostat, trouve la station la plus proche et vérifie si les seuils de déclenchement sont atteints.
                                </p>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Button
                                        variant="primary"
                                        onClick={handleCheckMeteostatObservations}
                                        disabled={meteostatLoading || !claimId}
                                    >
                                        {meteostatLoading ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-2" />
                                                Traitement en cours...
                                            </>
                                        ) : (
                                            <>
                                                <FiCloudRain className="me-2" />
                                                Créer/Mettre à jour les observations
                                            </>
                                        )}
                                    </Button>
                                    {meteostatData && (
                                        <Form.Check
                                            type="switch"
                                            id="toggle-meteostat-details"
                                            label="Afficher les détails"
                                            checked={showMeteostatDetails}
                                            onChange={(e) => setShowMeteostatDetails(e.target.checked)}
                                        />
                                    )}
                                </div>
                                {meteostatError && (
                                    <Alert variant="danger" className="mt-3">
                                        <strong>Erreur :</strong> {meteostatError}
                                    </Alert>
                                )}
                                {meteostatData && (
                                    <div className="mt-3">
                                        <Alert variant="success" className="mb-3">
                                            <strong>{t('pages.activityClaimDetail.messages.observationCreated')}</strong>
                                        </Alert>
                                        {showMeteostatDetails ? (
                                            <Card>
                                                <Card.Header>
                                                    <strong>Résultats détaillés</strong>
                                                </Card.Header>
                                                <Card.Body>
                                                    {meteostatData.message && (
                                                        <Alert variant="info" className="mb-3">
                                                            {meteostatData.message}
                                                        </Alert>
                                                    )}
                                                    {meteostatData.check_claims && (
                                                        <div className="mb-3">
                                                            <h6>Informations du check claim</h6>
                                                            {meteostatData.check_claims.check_claim_id && (
                                                                <p className="mb-2">
                                                                    <strong>ID du check claim :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.check_claims.check_claim_id}</span>
                                                                </p>
                                                            )}
                                                            {meteostatData.check_claims.is_triggered_observation !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Observation déclenchée :</strong>
                                                                    <Badge bg={meteostatData.check_claims.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                        {meteostatData.check_claims.is_triggered_observation ? 'Oui' : 'Non'}
                                                                    </Badge>
                                                                </p>
                                                            )}
                                                            {meteostatData.check_claims.observation_checked !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Observation vérifiée :</strong> {meteostatData.check_claims.observation_checked ? 'Oui' : 'Non'}
                                                                </p>
                                                            )}
                                                            {meteostatData.check_claims.count_hours_triggered !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Nombre d'heures déclenchées :</strong> {meteostatData.check_claims.count_hours_triggered}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                    {meteostatData.observation_info && (
                                                        <div className="mb-3">
                                                            <h6>Informations de l'observation</h6>
                                                            {meteostatData.observation_info.observation_id && (
                                                                <p className="mb-2">
                                                                    <strong>ID de l'observation :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.observation_info.observation_id}</span>
                                                                </p>
                                                            )}
                                                            {meteostatData.observation_info.data_source && (
                                                                <p className="mb-2">
                                                                    <strong>Source des données :</strong> {meteostatData.observation_info.data_source}
                                                                </p>
                                                            )}
                                                            {meteostatData.observation_info.total_mm_prc !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Total précipitations :</strong> {meteostatData.observation_info.total_mm_prc} mm
                                                                </p>
                                                            )}
                                                            {meteostatData.observation_info.station_name && (
                                                                <p className="mb-2">
                                                                    <strong>Station météorologique :</strong> {meteostatData.observation_info.station_name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                    {meteostatData.claim_info && (
                                                        <div className="mb-3">
                                                            <h6>Informations du sinistre</h6>
                                                            {meteostatData.claim_info.claim_id && (
                                                                <p className="mb-2">
                                                                    <strong>ID du sinistre :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.claim_info.claim_id}</span>
                                                                </p>
                                                            )}
                                                            {meteostatData.claim_info.claim_status && (
                                                                <p className="mb-2">
                                                                    <strong>Statut du sinistre :</strong> {meteostatData.claim_info.claim_status}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="mt-3">
                                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                        <pre style={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            fontSize: '0.85rem',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto',
                                                            backgroundColor: '#f8f9fa',
                                                            padding: '1rem',
                                                            borderRadius: '0.25rem'
                                                        }}>
                                                            {JSON.stringify(meteostatData, null, 2)}
                                                        </pre>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        ) : (
                                            <Card>
                                                <Card.Body>
                                                    <Row>
                                                        {meteostatData.check_claims?.is_triggered_observation !== undefined && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Observation déclenchée :</strong>
                                                                    <Badge bg={meteostatData.check_claims.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                        {meteostatData.check_claims.is_triggered_observation ? 'Oui' : 'Non'}
                                                                    </Badge>
                                                                </p>
                                                            </Col>
                                                        )}
                                                        {meteostatData.observation_info?.data_source && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Source :</strong> {meteostatData.observation_info.data_source}
                                                                </p>
                                                            </Col>
                                                        )}
                                                        {meteostatData.observation_info?.total_mm_prc !== undefined && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Précipitations :</strong> {meteostatData.observation_info.total_mm_prc} mm
                                                                </p>
                                                            </Col>
                                                        )}
                                                        {meteostatData.claim_info?.claim_status && (
                                                            <Col md={6}>
                                                                <p className="mb-2">
                                                                    <strong>Statut :</strong> {meteostatData.claim_info.claim_status}
                                                                </p>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                    {meteostatData.message && (
                                                        <Alert variant="info" className="mt-3">
                                                            {meteostatData.message}
                                                        </Alert>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    <Tab.Pane eventKey="main-info">
                        <Section title={t('pages.activityClaimDetail.sections.mainInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.claimCode')}</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['claim_code'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.status')}</Label><Value><FiCheckCircle className="me-1" />{get(claim, ['claim_status'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.stage')}</Label><Value>{get(claim, ['claim_stage'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.claimDate')}</Label><Value><FiCalendar className="me-1" />{get(claim, ['date_of_claim'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.amount')}</Label><Value><FaEuroSign className="me-1" />{get(claim, ['amount_of_claim'], '0')}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.activityMaintained')}</Label><Value>{get(claim, ['activity_maintained']) === 'true' ? t('common.yes') : t('common.no')}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.triggerBasedOn')}</Label><Value>{get(claim, ['trigger_based_on'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.openClosed')}</Label><Value><Badge bg={get(claim, ['open_closed_claims']) === 'open' ? 'success' : 'secondary'}>{get(claim, ['open_closed_claims'])}</Badge></Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.lastModified')}</Label><Value>{get(claim, ['claim_last_modified_at'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.modifiedBy')}</Label><Value>{get(claim, ['claim_last_modified_by'])}</Value></Col>
                                <Col md={4}><Label>{t('pages.activityClaimDetail.fields.ibanReceived')}</Label><Value>{get(claim, ['is_iban_received']) ? t('common.yes') : t('common.no')}</Value></Col>
                            </Row>
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.activityInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Nom</Label><Value>{get(claim, ['activity_info', 'name'])}</Value></Col>
                                <Col md={4}><Label>Catégorie</Label><Value>{get(claim, ['activity_info', 'category'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Date début</Label><Value><FiCalendar className="me-1" />{get(claim, ['activity_info', 'activity_start_date'])}</Value></Col>
                                <Col md={4}><Label>Heure début</Label><Value>{get(claim, ['activity_info', 'activity_start_time'])}</Value></Col>
                                <Col md={4}><Label>Date fin</Label><Value><FiCalendar className="me-1" />{get(claim, ['activity_info', 'activity_end_date'])}</Value></Col>
                                <Col md={4}><Label>Heure fin</Label><Value>{get(claim, ['activity_info', 'activity_end_time'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Adresse</Label><Value><FiMapPin className="me-1" />{get(claim, ['activity_info', 'activity_address'])}</Value></Col>
                                <Col md={2}><Label>Code postal</Label><Value>{get(claim, ['activity_info', 'activity_zip_code'])}</Value></Col>
                                <Col md={4}><Label>Ville</Label><Value>{get(claim, ['activity_info', 'activity_city'])}</Value></Col>
                                <Col md={4}><Label>Pays</Label><Value>{get(claim, ['activity_info', 'activity_country'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Prix</Label><Value>{get(claim, ['activity_info', 'activity_price'])}</Value></Col>
                                <Col md={4}><Label>Durée</Label><Value>{get(claim, ['activity_info', 'duration'])}</Value></Col>
                                <Col md={4}><Label>Code activité</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['activity_info', 'activity_code'])}</Value></Col>
                                <Col md={4}><Label>Numéro réservation</Label><Value>{get(claim, ['activity_info', 'reservation_number'])}</Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>Latitude</Label><Value>{get(claim, ['latitude'])}</Value></Col>
                                <Col md={4}><Label>Longitude</Label><Value>{get(claim, ['longitude'])}</Value></Col>
                            </Row>
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.clientInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Nom complet</Label><Value>{get(claim, ['client_info', 'first_name'])} {get(claim, ['client_info', 'last_name'])}</Value></Col>
                                <Col md={4}><Label>Email</Label><Value>{get(claim, ['client_info', 'email'])}</Value></Col>
                                <Col md={4}><Label>Téléphone</Label><Value>{get(claim, ['client_info', 'phone_number'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Code client</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['client_info', 'client_code'])}</Value></Col>
                                <Col md={4}><Label>Statut</Label><Value>{get(claim, ['client_info', 'status'])}</Value></Col>
                                <Col md={4}><Label>Date d'inscription</Label><Value>{get(claim, ['client_info', 'date_joined'])}</Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>Adresse</Label><Value>{get(claim, ['client_info', 'client_address'])}</Value></Col>
                                <Col md={2}><Label>Code postal</Label><Value>{get(claim, ['client_info', 'zip_code'])}</Value></Col>
                                <Col md={4}><Label>Ville</Label><Value>{get(claim, ['client_info', 'city'])}</Value></Col>
                                <Col md={4}><Label>Pays</Label><Value>{get(claim, ['client_info', 'country'])}</Value></Col>
                            </Row>
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.policyInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Code police</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['policy_info', 'policy_code'])}</Value></Col>
                                <Col md={4}><Label>Statut</Label><Value>{get(claim, ['policy_info', 'status'])}</Value></Col>
                                <Col md={4}><Label>Date UW</Label><Value>{get(claim, ['policy_info', 'uw_date'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Prix assurance (HT TSCA)</Label><Value>{get(claim, ['policy_info', 'insurance_price_excl_tsca'])}</Value></Col>
                                <Col md={4}><Label>Prix TTC (incl. TSCA)</Label><Value>{get(claim, ['policy_info', 'price_incl_tsca'])}</Value></Col>
                                <Col md={4}><Label>Montant TSCA</Label><Value>{get(claim, ['policy_info', 'tsca_amount'])}</Value></Col>
                                <Col md={4}><Label>TSCA (%)</Label><Value>{get(claim, ['policy_info', 'tsca'])}</Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>Ratio Orakle</Label><Value>{get(claim, ['policy_info', 'orakle_price_ratio_excl_tsca'])}</Value></Col>
                                <Col md={4}><Label>Langue préférée</Label><Value>{get(claim, ['policy_info', 'preferred_language'])}</Value></Col>
                                <Col md={4}><Label>Date soumission RIB</Label><Value>{get(claim, ['policy_info', 'date_rib_submission'])}</Value></Col>
                            </Row>
                            <PolicySumInsuredRows source={claim?.policy_info} Label={Label} Value={Value} colMd={6} />
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.partnerInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Nom</Label><Value>{get(claim, ['partner_info', 'partner_first_name'])} {get(claim, ['partner_info', 'partner_last_name'])}</Value></Col>
                                <Col md={4}><Label>Entreprise</Label><Value>{get(claim, ['partner_info', 'company_name'])}</Value></Col>
                                <Col md={4}><Label>Email</Label><Value>{get(claim, ['partner_info', 'email'])}</Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>Code</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['partner_info', 'code'])}</Value></Col>
                                <Col md={4}><Label>Téléphone</Label><Value>{get(claim, ['partner_info', 'phone_number'])}</Value></Col>
                                <Col md={4}><Label>Commission</Label><Value>{get(claim, ['partner_info', 'commission'])}</Value></Col>
                            </Row>
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.referenceInfo')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Nom référence</Label><Value>{get(claim, ['reference_info', 'reference_name'])}</Value></Col>
                                <Col md={4}><Label>Catégorie</Label><Value>{get(claim, ['reference_info', 'reference_category'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Code référence</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['reference_info', 'reference_code'])}</Value></Col>
                                <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(claim, ['reference_info', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                            </Row>
                            {claim?.reference_info?.SubCoverage && Array.isArray(claim.reference_info.SubCoverage) && claim.reference_info.SubCoverage.length > 0 && (
                                <div className="mt-3">
                                    <Label>Sous-couvertures</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Type déclenchement</th>
                                                <th>Trigger Low</th>
                                                <th>Trigger Sup</th>
                                                <th>Unité</th>
                                                <th>Ville</th>
                                                <th>Pays</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claim.reference_info.SubCoverage.map((sub, idx) => (
                                                <tr key={sub.sub_coverage_id || idx}>
                                                    <td>{get(sub, ['trigger_type'])}</td>
                                                    <td>{get(sub, ['trigger_low'])}</td>
                                                    <td>{get(sub, ['trigger_sup'])}</td>
                                                    <td>{get(sub, ['unit'])}</td>
                                                    <td>{get(sub, ['city'])}</td>
                                                    <td>{get(sub, ['country'])}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Section>

                        <Section title={t('pages.activityClaimDetail.sections.claimVerification')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Forecast déclenché</Label><Value>{get(claim, ['CheckClaims', 'is_triggered_forecast']) ? 'Oui' : 'Non'}</Value></Col>
                                <Col md={4}><Label>Forecast vérifié</Label><Value>{get(claim, ['CheckClaims', 'forecast_checked']) ? 'Oui' : 'Non'}</Value></Col>
                                <Col md={4}><Label>Observation vérifiée</Label><Value>{get(claim, ['CheckClaims', 'observation_checked']) ? 'Oui' : 'Non'}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Trigger Low</Label><Value>{get(claim, ['CheckClaims', 'trigger_low'])}</Value></Col>
                                <Col md={4}><Label>Trigger Sup</Label><Value>{get(claim, ['CheckClaims', 'check_trigger_sup'])}</Value></Col>
                                <Col md={4}><Label>Déclenchement cumulé</Label><Value>{get(claim, ['CheckClaims', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}</Value></Col>
                                <Col md={4}><Label>Date vérif. forecast</Label><Value>{get(claim, ['date_check_forecast'])}</Value></Col>
                            </Row>
                        </Section>

                        {/* Informations prévision */}
                        {claim?.forecast_info && claim.forecast_info.forecast_id && (
                            <Section title={t('pages.activityClaimDetail.sections.forecastInfo')}>
                                <Row className="mb-2">
                                    <Col md={4}><Label>ID Prévision</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['forecast_info', 'forecast_id'])}</Value></Col>
                                    <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['forecast_info', 'data_source'])}</Value></Col>
                                    <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(claim, ['forecast_info', 'total_mm_prc'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Coordonnées</Label><Value>{get(claim, ['forecast_info', 'latitude'])}, {get(claim, ['forecast_info', 'longitude'])}</Value></Col>
                                    <Col md={4}><Label>Date init</Label><Value>
                                        {get(claim, ['forecast_info', 'meteomatics_init_day'])}/
                                        {get(claim, ['forecast_info', 'meteomatics_init_month'])}/
                                        {get(claim, ['forecast_info', 'meteomatics_init_year'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Heure exécution (UTC)</Label><Value>{get(claim, ['forecast_info', 'meteomatics_run_hour_utc'])}</Value></Col>
                                </Row>
                                <Row>
                                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['forecast_info', 'forecast_last_modified_at'])}</Value></Col>
                                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['forecast_info', 'forecast_last_modified_by'])}</Value></Col>
                                </Row>
                                <WeatherDataSections
                                    weatherData={claim.forecast_info.weather_data}
                                    weatherTemp={claim.forecast_info.weather_temp}
                                    weatherWind={claim.forecast_info.weather_wind}
                                    enableCopy
                                    tableSize="sm"
                                    bordered
                                    className="mt-3"
                                    idPrefix="activity-claim"
                                />
                            </Section>
                        )}

                        {/* Informations d'observation - Dernière observation */}
                        {claim?.last_observation_info && claim.last_observation_info.last_observation_id && (
                            <Section title={t('pages.activityClaimDetail.sections.lastObservation')}>
                                <Row className="mb-2">
                                    <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['last_observation_info', 'last_observation_id'])}</Value></Col>
                                    <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['last_observation_info', 'last_data_source'])}</Value></Col>
                                    <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(claim, ['last_observation_info', 'last_total_mm_prc'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Date init</Label><Value>
                                        {get(claim, ['last_observation_info', 'last_observation_init_day'])}/
                                        {get(claim, ['last_observation_info', 'last_observation_init_month'])}/
                                        {get(claim, ['last_observation_info', 'last_observation_init_year'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Heure exécution</Label><Value>{get(claim, ['last_observation_info', 'last_observation_run_hour'])}</Value></Col>
                                    <Col md={4}><Label>Station ID</Label><Value>{get(claim, ['last_observation_info', 'last_station_id'])}</Value></Col>
                                    <Col md={4}><Label>Nom station</Label><Value>{get(claim, ['last_observation_info', 'last_station_name'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Coordonnées station</Label><Value>
                                        {get(claim, ['last_observation_info', 'last_latitude_station'])}, {get(claim, ['last_observation_info', 'last_longitude_station'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['last_observation_info', 'last_observation_last_modified_at'])}</Value></Col>
                                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['last_observation_info', 'last_observation_last_modified_by'])}</Value></Col>
                                </Row>
                                {claim.last_observation_info.last_observation_data && Array.isArray(claim.last_observation_info.last_observation_data) && claim.last_observation_info.last_observation_data.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Données d'observation</Label>
                                        <Table striped bordered hover size="sm" className="mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Date/Heure</th>
                                                    <th>Précipitation (mm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {claim.last_observation_info.last_observation_data.map((data, idx) => (
                                                    <tr key={idx}>
                                                        <td>{data.time}</td>
                                                        <td>{data.prcp}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Section>
                        )}

                        {/* Informations d'observation - Meteostat */}
                        {claim?.meteostat_observations_info && claim.meteostat_observations_info.meteostat_observation_id && (
                            <Section title={t('pages.activityClaimDetail.sections.meteostatObservations')}>
                                <Row className="mb-2">
                                    <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['meteostat_observations_info', 'meteostat_observation_id'])}</Value></Col>
                                    <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_data_source'])}</Value></Col>
                                    <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(claim, ['meteostat_observations_info', 'meteostat_total_mm_prc'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Date init</Label><Value>
                                        {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_day'])}/
                                        {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_month'])}/
                                        {get(claim, ['meteostat_observations_info', 'meteostat_observation_init_year'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Heure exécution</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_run_hour'])}</Value></Col>
                                    <Col md={4}><Label>Station ID</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_station_id'])}</Value></Col>
                                    <Col md={4}><Label>Nom station</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_station_name'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Coordonnées station</Label><Value>
                                        {get(claim, ['meteostat_observations_info', 'meteostat_latitude_station'])}, {get(claim, ['meteostat_observations_info', 'meteostat_longitude_station'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_last_modified_at'])}</Value></Col>
                                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['meteostat_observations_info', 'meteostat_observation_last_modified_by'])}</Value></Col>
                                </Row>
                                {claim.meteostat_observations_info.meteostat_observation_data && Array.isArray(claim.meteostat_observations_info.meteostat_observation_data) && claim.meteostat_observations_info.meteostat_observation_data.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Données d'observation</Label>
                                        <Table striped bordered hover size="sm" className="mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Date/Heure</th>
                                                    <th>Précipitation (mm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {claim.meteostat_observations_info.meteostat_observation_data.map((data, idx) => (
                                                    <tr key={idx}>
                                                        <td>{data.time}</td>
                                                        <td>{data.prcp}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Section>
                        )}

                        {/* Informations d'observation - Meteomatics */}
                        {claim?.meteomatics_observations_info && claim.meteomatics_observations_info.meteomatics_observation_id && (
                            <Section title={t('pages.activityClaimDetail.sections.meteomaticsObservations')}>
                                <Row className="mb-2">
                                    <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_id'])}</Value></Col>
                                    <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_data_source'])}</Value></Col>
                                    <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(claim, ['meteomatics_observations_info', 'meteomatics_total_mm_prc'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Date init</Label><Value>
                                        {get(claim, ['meteomatics_observations_info', 'meteomatics_observation_init_day'])}/
                                        {get(claim, ['meteomatics_observations_info', 'meteomatics_observation_init_month'])}/
                                        {get(claim, ['meteomatics_observations_info', 'meteomatics_observation_init_year'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Heure exécution</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_run_hour'])}</Value></Col>
                                    <Col md={4}><Label>Station ID</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_station_id'])}</Value></Col>
                                    <Col md={4}><Label>Nom station</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_station_name'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Coordonnées station</Label><Value>
                                        {get(claim, ['meteomatics_observations_info', 'meteomatics_latitude_station'])}, {get(claim, ['meteomatics_observations_info', 'meteomatics_longitude_station'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_last_modified_at'])}</Value></Col>
                                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['meteomatics_observations_info', 'meteomatics_observation_last_modified_by'])}</Value></Col>
                                </Row>
                                {claim.meteomatics_observations_info.meteomatics_observation_data && Array.isArray(claim.meteomatics_observations_info.meteomatics_observation_data) && claim.meteomatics_observations_info.meteomatics_observation_data.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Données d'observation</Label>
                                        <Table striped bordered hover size="sm" className="mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Date/Heure</th>
                                                    <th>Précipitation (mm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {claim.meteomatics_observations_info.meteomatics_observation_data.map((data, idx) => (
                                                    <tr key={idx}>
                                                        <td>{data.time}</td>
                                                        <td>{data.prcp}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Section>
                        )}

                        {/* Informations d'observation - Meteofrance */}
                        {claim?.meteofrance_observations_info && claim.meteofrance_observations_info.meteofrance_observation_id && (
                            <Section title={t('pages.activityClaimDetail.sections.meteofranceObservations')}>
                                <Row className="mb-2">
                                    <Col md={4}><Label>ID Observation</Label><Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_id'])}</Value></Col>
                                    <Col md={4}><Label>Source de données</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_data_source'])}</Value></Col>
                                    <Col md={4}><Label>Total mm précipitation</Label><Value><FiCloud className="me-1" />{get(claim, ['meteofrance_observations_info', 'meteofrance_total_mm_prc'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Date init</Label><Value>
                                        {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_day'])}/
                                        {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_month'])}/
                                        {get(claim, ['meteofrance_observations_info', 'meteofrance_observation_init_year'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Heure exécution</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_run_hour'])}</Value></Col>
                                    <Col md={4}><Label>Station ID</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_station_id'])}</Value></Col>
                                    <Col md={4}><Label>Nom station</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_station_name'])}</Value></Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col md={4}><Label>Coordonnées station</Label><Value>
                                        {get(claim, ['meteofrance_observations_info', 'meteofrance_latitude_station'])}, {get(claim, ['meteofrance_observations_info', 'meteofrance_longitude_station'])}
                                    </Value></Col>
                                    <Col md={4}><Label>Dernière modification</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_last_modified_at'])}</Value></Col>
                                    <Col md={4}><Label>Modifié par</Label><Value>{get(claim, ['meteofrance_observations_info', 'meteofrance_observation_last_modified_by'])}</Value></Col>
                                </Row>
                                {claim.meteofrance_observations_info.meteofrance_observation_data && Array.isArray(claim.meteofrance_observations_info.meteofrance_observation_data) && claim.meteofrance_observations_info.meteofrance_observation_data.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Données d'observation</Label>
                                        <Table striped bordered hover size="sm" className="mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Date/Heure</th>
                                                    <th>Précipitation (mm)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {claim.meteofrance_observations_info.meteofrance_observation_data.map((data, idx) => (
                                                    <tr key={idx}>
                                                        <td>{data.time}</td>
                                                        <td>{data.prcp}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Section>
                        )}

                        <Section title={t('pages.activityClaimDetail.sections.choiceAndPayment')}>
                            <Row className="mb-2">
                                <Col md={4}><Label>Choix confirmé</Label><Value>{get(claim, ['is_confirmed']) ? 'Oui' : 'Non'}</Value></Col>
                                <Col md={4}><Label>Date choix</Label><Value>{get(claim, ['choice_date'])}</Value></Col>
                                <Col md={4}><Label>Date confirmation choix</Label><Value>{get(claim, ['choice_confirmation_date'])}</Value></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={4}><Label>Choix signé</Label><Value>{get(claim, ['activity_choice_signed'])}</Value></Col>
                                <Col md={4}><Label>Date signature choix</Label><Value>{get(claim, ['choice_signature_date'])}</Value></Col>
                                <Col md={4}><Label>Délai dépassé</Label><Value><Badge bg={get(claim, ['deadline_past']) ? 'danger' : 'success'}>{get(claim, ['deadline_past']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                            </Row>
                            <Row>
                                <Col md={4}><Label>Date paiement</Label><Value>{get(claim, ['payment_date'])}</Value></Col>
                                <Col md={4}><Label>Mode paiement</Label><Value>{get(claim, ['payment_mode'])}</Value></Col>
                            </Row>
                        </Section>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Modal pour afficher le token généré */}
            <Modal show={showTokenModal} onHide={handleCloseTokenModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiKey className="me-2" />
                        Token d'information du sinistre
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tokenLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Génération du token en cours...</p>
                        </div>
                    ) : tokenError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {tokenError}
                        </Alert>
                    ) : tokenData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Token généré avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations du token</strong>
                                </Card.Header>
                                <Card.Body>
                                    {tokenData.token && (
                                        <div className="mb-3">
                                            <Form.Label><strong>Token :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={tokenData.token}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(tokenData.token);
                                                }}
                                            >
                                                Copier le token
                                            </Button>
                                        </div>
                                    )}
                                    {tokenData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {tokenData.claim_id}
                                        </p>
                                    )}
                                    {tokenData.expires_at && (
                                        <p className="mb-2">
                                            <strong>Expire le :</strong> {tokenData.expires_at}
                                        </p>
                                    )}
                                    {tokenData.url && (
                                        <div className="mb-3">
                                            <Form.Label><strong>URL d'accès :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={tokenData.url}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(tokenData.url);
                                                }}
                                            >
                                                Copier l'URL
                                            </Button>
                                        </div>
                                    )}
                                    {tokenData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {tokenData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(tokenData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseTokenModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour afficher le lien de confirmation généré */}
            <Modal show={showConfirmationLinkModal} onHide={handleCloseConfirmationLinkModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Lien de confirmation d'activité
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirmationLinkLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Génération du lien de confirmation en cours...</p>
                        </div>
                    ) : confirmationLinkError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {confirmationLinkError}
                        </Alert>
                    ) : confirmationLinkData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Lien de confirmation généré avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations du lien de confirmation</strong>
                                </Card.Header>
                                <Card.Body>
                                    {confirmationLinkData.url && (
                                        <div className="mb-3">
                                            <Form.Label><strong>URL de confirmation :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={confirmationLinkData.url}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2 me-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(confirmationLinkData.url);
                                                }}
                                            >
                                                Copier l'URL
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    window.open(confirmationLinkData.url, '_blank');
                                                }}
                                            >
                                                Ouvrir dans un nouvel onglet
                                            </Button>
                                        </div>
                                    )}
                                    {confirmationLinkData.policy_code && (
                                        <p className="mb-2">
                                            <strong>Code police :</strong> {confirmationLinkData.policy_code}
                                        </p>
                                    )}
                                    {confirmationLinkData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {confirmationLinkData.claim_id}
                                        </p>
                                    )}
                                    {confirmationLinkData.check_claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du check claim :</strong> {confirmationLinkData.check_claim_id}
                                        </p>
                                    )}
                                    {confirmationLinkData.client_id && (
                                        <p className="mb-2">
                                            <strong>ID du client :</strong> {confirmationLinkData.client_id}
                                        </p>
                                    )}
                                    {confirmationLinkData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {confirmationLinkData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(confirmationLinkData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseConfirmationLinkModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour afficher l'URL de prévision déclenchée générée */}
            <Modal show={showForecastTriggeredModal} onHide={handleCloseForecastTriggeredModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCloud className="me-2" />
                        URL de prévision déclenchée
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {forecastTriggeredLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Génération de l'URL de prévision déclenchée en cours...</p>
                        </div>
                    ) : forecastTriggeredError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {forecastTriggeredError}
                        </Alert>
                    ) : forecastTriggeredData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>URL de prévision déclenchée générée avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations de l'URL de prévision déclenchée</strong>
                                </Card.Header>
                                <Card.Body>
                                    {forecastTriggeredData.forecast_triggered_url && (
                                        <div className="mb-3">
                                            <Form.Label><strong>URL de prévision déclenchée :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={forecastTriggeredData.forecast_triggered_url}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2 me-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(forecastTriggeredData.forecast_triggered_url);
                                                }}
                                            >
                                                Copier l'URL
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    window.open(forecastTriggeredData.forecast_triggered_url, '_blank');
                                                }}
                                            >
                                                Ouvrir dans un nouvel onglet
                                            </Button>
                                        </div>
                                    )}
                                    {forecastTriggeredData.validation_token && (
                                        <div className="mb-3">
                                            <Form.Label><strong>Token de validation :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={forecastTriggeredData.validation_token}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(forecastTriggeredData.validation_token);
                                                }}
                                            >
                                                Copier le token
                                            </Button>
                                        </div>
                                    )}
                                    {forecastTriggeredData.token_info && (
                                        <div className="mb-3">
                                            <Form.Label><strong>Token d'information :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={forecastTriggeredData.token_info}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(forecastTriggeredData.token_info);
                                                }}
                                            >
                                                Copier le token
                                            </Button>
                                        </div>
                                    )}
                                    {forecastTriggeredData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {forecastTriggeredData.claim_id}
                                        </p>
                                    )}
                                    {forecastTriggeredData.client_id && (
                                        <p className="mb-2">
                                            <strong>ID du client :</strong> {forecastTriggeredData.client_id}
                                        </p>
                                    )}
                                    {forecastTriggeredData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {forecastTriggeredData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(forecastTriggeredData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseForecastTriggeredModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour afficher le token de confirmation d'activité généré */}
            <Modal show={showActivityConfirmationModal} onHide={handleCloseActivityConfirmationModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Token de confirmation d'activité
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activityConfirmationLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Génération du token de confirmation d'activité en cours...</p>
                        </div>
                    ) : activityConfirmationError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {activityConfirmationError}
                        </Alert>
                    ) : activityConfirmationData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Token de confirmation d'activité généré avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations du token de confirmation</strong>
                                </Card.Header>
                                <Card.Body>
                                    {activityConfirmationData.validation_token && (
                                        <div className="mb-3">
                                            <Form.Label><strong>Token de validation :</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={activityConfirmationData.validation_token}
                                                readOnly
                                                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(activityConfirmationData.validation_token);
                                                }}
                                            >
                                                Copier le token
                                            </Button>
                                        </div>
                                    )}
                                    {activityConfirmationData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {activityConfirmationData.claim_id}
                                        </p>
                                    )}
                                    {activityConfirmationData.client_id && (
                                        <p className="mb-2">
                                            <strong>ID du client :</strong> {activityConfirmationData.client_id}
                                        </p>
                                    )}
                                    {activityConfirmationData.activity_id && (
                                        <p className="mb-2">
                                            <strong>ID de l'activité :</strong> {activityConfirmationData.activity_id}
                                        </p>
                                    )}
                                    {activityConfirmationData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {activityConfirmationData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(activityConfirmationData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseActivityConfirmationModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour afficher le lien de paiement généré */}
            <Modal show={showPaymentLinkModal} onHide={handleClosePaymentLinkModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiGift className="me-2" />
                        Lien de paiement
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {paymentLinkLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Génération du lien de paiement en cours...</p>
                        </div>
                    ) : paymentLinkError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {paymentLinkError}
                        </Alert>
                    ) : paymentLinkData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Lien de paiement généré avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations du paiement</strong>
                                </Card.Header>
                                <Card.Body>
                                    {paymentLinkData.payment && (
                                        <>
                                            {paymentLinkData.payment.payment_link && (
                                                <div className="mb-3">
                                                    <Form.Label><strong>Lien de paiement Stripe :</strong></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={paymentLinkData.payment.payment_link}
                                                        readOnly
                                                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="mt-2 me-2"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(paymentLinkData.payment.payment_link);
                                                        }}
                                                    >
                                                        Copier le lien
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="mt-2"
                                                        onClick={() => {
                                                            window.open(paymentLinkData.payment.payment_link, '_blank');
                                                        }}
                                                    >
                                                        Ouvrir dans un nouvel onglet
                                                    </Button>
                                                </div>
                                            )}
                                            {paymentLinkData.payment.payment_code && (
                                                <p className="mb-2">
                                                    <strong>Code paiement :</strong> {paymentLinkData.payment.payment_code}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.payment_id && (
                                                <p className="mb-2">
                                                    <strong>ID du paiement :</strong> {paymentLinkData.payment.payment_id}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.policy_code && (
                                                <p className="mb-2">
                                                    <strong>Code police :</strong> {paymentLinkData.payment.policy_code}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.policy_id && (
                                                <p className="mb-2">
                                                    <strong>ID de la police :</strong> {paymentLinkData.payment.policy_id}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.payment_date && (
                                                <p className="mb-2">
                                                    <strong>Date de paiement :</strong> {paymentLinkData.payment.payment_date}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.payment_mode && (
                                                <p className="mb-2">
                                                    <strong>Mode de paiement :</strong> {paymentLinkData.payment.payment_mode}
                                                </p>
                                            )}
                                            {paymentLinkData.payment.payment_status !== undefined && (
                                                <p className="mb-2">
                                                    <strong>Statut du paiement :</strong> {paymentLinkData.payment.payment_status ? 'Payé' : 'En attente'}
                                                </p>
                                            )}
                                        </>
                                    )}
                                    {paymentLinkData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {paymentLinkData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(paymentLinkData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClosePaymentLinkModal}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal pour Choix du client annuler / maintenir */}
            <Modal show={showActivityStatusModal} onHide={handleCloseActivityStatusModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiEdit className="me-2" />
                        Choix du client annuler / maintenir
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activityStatusLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Mise à jour du statut en cours...</p>
                        </div>
                    ) : activityStatusError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {activityStatusError}
                        </Alert>
                    ) : activityStatusData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Statut d'activité mis à jour avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations mises à jour</strong>
                                </Card.Header>
                                <Card.Body>
                                    {activityStatusData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {activityStatusData.claim_id}
                                        </p>
                                    )}
                                    {activityStatusData.activity_status && (
                                        <p className="mb-2">
                                            <strong>Statut d'activité :</strong> {activityStatusData.activity_status}
                                        </p>
                                    )}
                                    {activityStatusData.amount_of_claim !== undefined && (
                                        <p className="mb-2">
                                            <strong>Montant du sinistre :</strong> {activityStatusData.amount_of_claim} €
                                        </p>
                                    )}
                                    {activityStatusData.updated_at && (
                                        <p className="mb-2">
                                            <strong>Date de mise à jour :</strong> {activityStatusData.updated_at}
                                        </p>
                                    )}
                                    {activityStatusData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {activityStatusData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(activityStatusData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label><strong>Sélectionner le statut d'activité :</strong></Form.Label>
                                <Form.Select
                                    value={selectedActivityStatus}
                                    onChange={(e) => setSelectedActivityStatus(e.target.value)}
                                    required
                                >
                                    <option value="">Sélectionner un statut...</option>
                                    <option value="Maintenir mon activité">Maintenir mon activité</option>
                                    <option value="Annuler mon activité">Annuler mon activité</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Sélectionnez si l'activité doit être maintenue ou annulée pour ce sinistre.
                                </Form.Text>
                            </Form.Group>
                            {claim && (
                                <Alert variant="info" className="mt-3">
                                    <strong>Statut actuel :</strong> {claim.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité'}
                                </Alert>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {activityStatusData ? (
                        <Button variant="secondary" onClick={handleCloseActivityStatusModal}>
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={handleCloseActivityStatusModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateActivityStatus}
                                disabled={!selectedActivityStatus || activityStatusLoading}
                            >
                                {activityStatusLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Mise à jour...
                                    </>
                                ) : (
                                    'Mettre à jour'
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal pour mettre à jour le statut du sinistre */}
            <Modal show={showClaimStatusModal} onHide={handleCloseClaimStatusModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiEdit className="me-2" />
                        Mettre à jour le statut du sinistre
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {claimStatusLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Mise à jour du statut en cours...</p>
                        </div>
                    ) : claimStatusError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {claimStatusError}
                        </Alert>
                    ) : claimStatusData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Statut du sinistre mis à jour avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations mises à jour</strong>
                                </Card.Header>
                                <Card.Body>
                                    {claimStatusData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {claimStatusData.claim_id}
                                        </p>
                                    )}
                                    {claimStatusData.claim_status && (
                                        <p className="mb-2">
                                            <strong>Statut du sinistre :</strong> {claimStatusData.claim_status}
                                        </p>
                                    )}
                                    {claimStatusData.claim_stage && (
                                        <p className="mb-2">
                                            <strong>Étape du sinistre :</strong> {claimStatusData.claim_stage}
                                        </p>
                                    )}
                                    {claimStatusData.payment_date && (
                                        <p className="mb-2">
                                            <strong>Date de paiement :</strong> {claimStatusData.payment_date}
                                        </p>
                                    )}
                                    {claimStatusData.payment_mode && (
                                        <p className="mb-2">
                                            <strong>Mode de paiement :</strong> {claimStatusData.payment_mode}
                                        </p>
                                    )}
                                    {claimStatusData.updated_at && (
                                        <p className="mb-2">
                                            <strong>Date de mise à jour :</strong> {claimStatusData.updated_at}
                                        </p>
                                    )}
                                    {claimStatusData.updated_by && (
                                        <p className="mb-2">
                                            <strong>Mis à jour par :</strong> {claimStatusData.updated_by}
                                        </p>
                                    )}
                                    {claimStatusData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {claimStatusData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(claimStatusData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label><strong>Sélectionner le statut du sinistre :</strong></Form.Label>
                                <Form.Select
                                    value={selectedClaimStatus}
                                    onChange={(e) => {
                                        setSelectedClaimStatus(e.target.value);
                                        // Réinitialiser les champs de paiement si le statut change
                                        if (e.target.value !== 'Paid') {
                                            setPaymentDate('');
                                            setPaymentDatePicker(null);
                                            setPaymentMode('');
                                        }
                                    }}
                                    required
                                >
                                    <option value="">Sélectionner un statut...</option>
                                    <option value="Pre-validated">Pre-validated</option>
                                    <option value="Not pre-validated">Not pre-validated</option>
                                    <option value="Validated">Validated</option>
                                    <option value="Not validated">Not validated</option>
                                    <option value="Documents received">Documents received</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Expired">Expired</option>
                                    <option value="No answer yet">No answer yet</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Sélectionnez le nouveau statut du sinistre. Si vous choisissez "Paid", vous devrez fournir la date et le mode de paiement.
                                </Form.Text>
                            </Form.Group>

                            {selectedClaimStatus === 'Paid' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Date de paiement (dd/mm/yyyy) :</strong></Form.Label>
                                        <DatePicker
                                            selected={paymentDatePicker}
                                            onChange={handlePaymentDateChange}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Sélectionner une date"
                                            className="form-control"
                                            required
                                            isClearable
                                            showYearDropdown
                                            showMonthDropdown
                                            dropdownMode="select"
                                            maxDate={new Date()}
                                        />
                                        <Form.Text className="text-muted">
                                            Sélectionnez la date de paiement (format : jj/mm/aaaa)
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Mode de paiement :</strong></Form.Label>
                                        <Form.Select
                                            value={paymentMode}
                                            onChange={(e) => setPaymentMode(e.target.value)}
                                            required
                                        >
                                            <option value="">Sélectionner un mode de paiement...</option>
                                            <option value="strip">Stripe</option>
                                            <option value="voucher">Voucher</option>
                                            <option value="cash">Cash</option>
                                            <option value="transfer">Transfer</option>
                                        </Form.Select>
                                    </Form.Group>
                                </>
                            )}

                            {claim && (
                                <Alert variant="info" className="mt-3">
                                    <strong>Statut actuel :</strong> {claim.claim_status || 'N/A'}<br />
                                    <strong>Étape actuelle :</strong> {claim.claim_stage || 'N/A'}
                                </Alert>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {claimStatusData ? (
                        <Button variant="secondary" onClick={handleCloseClaimStatusModal}>
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={handleCloseClaimStatusModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateClaimStatus}
                                disabled={!selectedClaimStatus || claimStatusLoading || (selectedClaimStatus === 'Paid' && (!paymentDate || !paymentMode))}
                            >
                                {claimStatusLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Mise à jour...
                                    </>
                                ) : (
                                    'Mettre à jour'
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal pour confirmer le statut d'activité */}
            <Modal show={showConfirmActivityModal} onHide={handleCloseConfirmActivityModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Confirmer le statut d'activité
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {confirmActivityLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Confirmation du statut d'activité en cours...</p>
                        </div>
                    ) : confirmActivityError ? (
                        <Alert variant="danger">
                            <strong>Erreur :</strong> {confirmActivityError}
                        </Alert>
                    ) : confirmActivityData ? (
                        <div>
                            <Alert variant="success" className="mb-3">
                                <strong>Statut d'activité confirmé avec succès !</strong>
                            </Alert>
                            <Card>
                                <Card.Header>
                                    <strong>Informations de confirmation</strong>
                                </Card.Header>
                                <Card.Body>
                                    {confirmActivityData.claim_id && (
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {confirmActivityData.claim_id}
                                        </p>
                                    )}
                                    {confirmActivityData.is_confirmed !== undefined && (
                                        <p className="mb-2">
                                            <strong>Statut de confirmation :</strong> {confirmActivityData.is_confirmed ? 'Confirmé' : 'Non confirmé'}
                                        </p>
                                    )}
                                    {confirmActivityData.choice_confirmation_date && (
                                        <p className="mb-2">
                                            <strong>Date de confirmation du choix :</strong> {confirmActivityData.choice_confirmation_date}
                                        </p>
                                    )}
                                    {confirmActivityData.updated_at && (
                                        <p className="mb-2">
                                            <strong>Date de mise à jour :</strong> {confirmActivityData.updated_at}
                                        </p>
                                    )}
                                    {confirmActivityData.updated_by && (
                                        <p className="mb-2">
                                            <strong>Confirmé par :</strong> {confirmActivityData.updated_by}
                                        </p>
                                    )}
                                    {confirmActivityData.message && (
                                        <Alert variant="info" className="mt-3">
                                            {confirmActivityData.message}
                                        </Alert>
                                    )}
                                    <div className="mt-3">
                                        <Form.Label><strong>Données complètes :</strong></Form.Label>
                                        <pre style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: '0.85rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            backgroundColor: '#f8f9fa',
                                            padding: '1rem',
                                            borderRadius: '0.25rem'
                                        }}>
                                            {JSON.stringify(confirmActivityData, null, 2)}
                                        </pre>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ) : (
                        <div>
                            <Alert variant="warning" className="mb-3">
                                <strong>Attention :</strong> Cette action va confirmer le statut d'activité du sinistre.
                            </Alert>
                            {claim && (
                                <Card>
                                    <Card.Header>
                                        <strong>Informations du sinistre</strong>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="mb-2">
                                            <strong>ID du sinistre :</strong> {claim.claim_id || claimId}
                                        </p>
                                        <p className="mb-2">
                                            <strong>Statut actuel de confirmation :</strong> {claim.is_confirmed ? 'Confirmé' : 'Non confirmé'}
                                        </p>
                                        {claim.activity_maintained && (
                                            <p className="mb-2">
                                                <strong>Choix du client :</strong> {claim.activity_maintained === 'true' ? 'Maintenir mon activité' : 'Annuler mon activité'}
                                            </p>
                                        )}
                                        {claim.choice_date && (
                                            <p className="mb-2">
                                                <strong>Date du choix :</strong> {claim.choice_date}
                                            </p>
                                        )}
                                    </Card.Body>
                                </Card>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {confirmActivityData ? (
                        <Button variant="secondary" onClick={handleCloseConfirmActivityModal}>
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={handleCloseConfirmActivityModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleConfirmActivityStatus}
                                disabled={confirmActivityLoading || !claimId}
                            >
                                {confirmActivityLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Confirmation...
                                    </>
                                ) : (
                                    <>
                                        <FiCheckCircle className="me-2" />
                                        Confirmer
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminActivityClaimDetail;


